import GLOBALS from './GLOBALS.json';

// User flow states
export const USER_FLOW_STATES = {
  TERMS_PENDING: 'terms_pending',
  ONBOARDING_PENDING: 'onboarding_pending', 
  COMPLETED: 'completed'
};

export const UserFlowService = {
  
  // Roles that require onboarding
  ROLES_WITH_ONBOARDING: ['user_manager', 'clinician', 'institutional_clinician', 'evaluator'],
  
  // Set the current user flow state
  setUserFlowState: function(state) {
    sessionStorage.setItem(GLOBALS.SESSION_KEYS.USER_FLOW_STATE, state);
  },
  
  // Get the current user flow state
  getUserFlowState: function() {
    return sessionStorage.getItem(GLOBALS.SESSION_KEYS.USER_FLOW_STATE);
  },
  
  // Check if user needs to accept terms
  needsToAcceptTerms: function() {
    const tandcAccepted = sessionStorage.getItem(GLOBALS.SESSION_KEYS.TANDC_ACCEPTED);
    return tandcAccepted === '0'; // 0 means needs to accept terms (still pending)
  },
  
  // Get onboarding flags from backend
  getOnboardingFlags: function() {
    return JSON.parse(sessionStorage.getItem(GLOBALS.SESSION_KEYS.ONBOARDING_FLAGS) || '[]');
  },

  // Check if a specific role needs onboarding
  needsRoleOnboarding: function(role) {
    // Only check roles that actually have onboarding
    if (!this.ROLES_WITH_ONBOARDING.includes(role)) {
      return false;
    }
    
    const roles = JSON.parse(sessionStorage.getItem(GLOBALS.SESSION_KEYS.USER_ROLES) || '[]');
    const onboardingFlags = this.getOnboardingFlags();
    
    // Check if user has the role and if it's not in onboarding_flags
    return roles.includes(role) && !onboardingFlags.includes(role);
  },

  // Check if user manager needs onboarding
  needsOnboarding: function() {
    return this.needsRoleOnboarding('user_manager');
  },
  
  // Check if evaluator needs onboarding
  needsEvaluatorOnboarding: function() {
    return this.needsRoleOnboarding('evaluator');
  },
  
  // Check if clinician needs onboarding
  needsClinicianOnboarding: function() {
    return this.needsRoleOnboarding('clinician') || this.needsRoleOnboarding('institutional_clinician');
  },

  // Check if user is first-time (no onboarding completed yet)
  isFirstTimeUser: function() {
    const onboardingFlags = this.getOnboardingFlags();
    return onboardingFlags.length === 0;
  },

  // Check if user has completed name step (has first and last name)
  hasCompletedNameStep: function() {
    const firstName = sessionStorage.getItem(GLOBALS.SESSION_KEYS.USER_FNAME);
    const lastName = sessionStorage.getItem(GLOBALS.SESSION_KEYS.USER_LNAME);
    return !!(firstName && lastName && firstName.trim() !== '' && lastName.trim() !== '');
  },

  // Get all roles that need onboarding
  getRolesNeedingOnboarding: function() {
    const roles = JSON.parse(sessionStorage.getItem(GLOBALS.SESSION_KEYS.USER_ROLES) || '[]');
    const onboardingFlags = this.getOnboardingFlags();
    
    // Only consider roles that actually have onboarding
    return roles.filter(role => {
      if (!this.ROLES_WITH_ONBOARDING.includes(role)) {
        return false;
      }
      
      // Special handling for clinician roles - treat as equivalent
      if (role === 'clinician' || role === 'institutional_clinician') {
        const hasClinicianFlag = onboardingFlags.includes('clinician') || onboardingFlags.includes('institutional_clinician');
        return !hasClinicianFlag;
      }
      
      // For other roles, check if flag exists
      return !onboardingFlags.includes(role);
    });
  },

  // Get the next onboarding to show based on priority
  getNextOnboarding: function() {
    const rolesNeedingOnboarding = this.getRolesNeedingOnboarding();
    
    if (rolesNeedingOnboarding.length === 0) {
      return null; // All onboardings completed
    }

    // Priority order: user_manager > clinician > evaluator
    if (rolesNeedingOnboarding.includes('user_manager')) {
      return 'user_manager';
    }
    if (rolesNeedingOnboarding.includes('clinician') || rolesNeedingOnboarding.includes('institutional_clinician')) {
      return 'clinician';
    }
    if (rolesNeedingOnboarding.includes('evaluator')) {
      return 'evaluator';
    }

    return null;
  },

  // Check if all onboardings are completed
  areAllOnboardingsCompleted: function() {
    const rolesNeedingOnboarding = this.getRolesNeedingOnboarding();
    return rolesNeedingOnboarding.length === 0;
  },
  
  // Determine what the user should be redirected to
  getRequiredRedirect: function() {
    
    // Step 1: Check if user needs to accept terms
    if (this.needsToAcceptTerms()) {
      return '/onboarding/terms_and_conditions';
    }
    
    // Step 2: Check if all onboardings are completed
    if (this.areAllOnboardingsCompleted()) {
      return null; // No redirect needed - go to dashboard
    }
    
    // Step 3: Redirect to unified onboarding (includes name step if needed)
    return '/onboarding/unified';
  },
  
  // Check if current path is allowed for user's state
  isPathAllowed: function(currentPath) {
    const requiredRedirect = this.getRequiredRedirect();
    
    // If no redirect needed, all paths are allowed
    if (!requiredRedirect) {
      return true;
    }
    
    // If redirect needed, only allow the required path and auth paths
    const allowedPaths = [
      requiredRedirect,
      '/auth/sign-in',
      '/auth/sign-up', 
      '/auth/forgot-password',
      '/auth/404',
      '/auth/500'
    ];
    
    return allowedPaths.some(path => currentPath.startsWith(path));
  },
  
  // Clear user flow state (on logout)
  clearUserFlowState: function() {
    sessionStorage.removeItem(GLOBALS.SESSION_KEYS.USER_FLOW_STATE);
  }
  
};
